class AlgoliaInfiniteHits extends HTMLElement {
  constructor() {
    super();

    this.connectedCallback = this.connectedCallback.bind(this);
    this.createWidget = this.createWidget.bind(this);
    this.mergeMarketingTiles = this.mergeMarketingTiles.bind(this);
    this.render = this.render.bind(this);
    this.renderContainer = this.renderContainer.bind(this);
    this.renderContent = this.renderContent.bind(this);
    this.renderWrapper = this.renderWrapper.bind(this);
  }

  /**
   * Initializes settings based on custom attributes when the element is connected to the DOM.
   * @returns {void}
   */
  connectedCallback() {
    this.settings = {
      columnsDesktop: this.getAttribute('columns-desktop'),
      columnsMobile: this.getAttribute('columns-mobile'),
      columnsTablet: this.getAttribute('columns-tablet'),
      mediaAspectRatio: this.getAttribute('media-aspect-ratio'),
      productsGridSystemDesktop: this.getAttribute('products-grid-system-desktop'),
      productsPerPage: this.getAttribute('products-per-page')
    }
  }

  /**
   * Creates an instance of the infinite hits widget.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Object} instances - Instances object containing algolia instances.
   * @returns {Object} The created infinite hits widget instance.
   */
  createWidget(configuration, instances) {
    const { createInfiniteHitsSessionStorageCache } = instantsearch;
    const { connectInfiniteHits } = instances.instantSearch.connectors;

    const cache = createInfiniteHitsSessionStorageCache();
    const connectedInfiniteHits = connectInfiniteHits((renderOptions, isFirstRender) => this.render(instances, isFirstRender, renderOptions));

    const selector = `.${configuration.container}`;
    const container = document.querySelector(selector);

    const infiniteHitsWidget = connectedInfiniteHits({ cache, container });
    return infiniteHitsWidget;
  }

  /**
   * Merges marketing tiles with the hits array, updating their positions.
   * @param {Array} hits - The array of hits to merge with marketing tiles.
   * @returns {Array} - The updated hits array with tiles inserted.
   */
  mergeMarketingTiles(hits) {
    /* When marketing tiles are not in use, no adjustments to hits are necessary. */
    const tilesContainer = document.getElementById('marketing-tiles');
    if (!tilesContainer) return hits;

    /* To update the tiles, we clone the existing ones for modification. */
    const clonedTiles = tilesContainer.cloneNode(true);

    /* Retrieve static tile nodes from the cloned container. */
    const clonedTileNodes = clonedTiles.children;
    if (!clonedTileNodes.length) return hits;

    /* Convert the static tile nodes into an array. */
    const tilesArray = Array.from(clonedTileNodes);

    /* Only tiles within the range of returned hits will be rendered on the page based on their position. */
    const filteredTiles = tilesArray.filter((tile) => tile.dataset.position <= hits.length);

    /* Tiles should be sorted beforehand to ensure correct insertion order. */
    const sortedTiles = filteredTiles.sort((a, b) => {
      const aPosition = a.dataset.position ? parseInt(a.dataset.position) : Infinity;
      const bPosition = b.dataset.position ? parseInt(b.dataset.position) : Infinity;
      return aPosition - bPosition;
    });

    /* Track positions of used tiles to prevent overlaps and preserve the right position. */
    const tilesInUse = new Map();

    /* Store original hits length. */
    const mergedHits = hits.slice();

    /* Iterate through sorted tiles to insert them into the existing merged hits array. */
    sortedTiles.forEach((tile) => {
      const position = parseInt(tile.dataset.position);

      /* Prevent site manager from inserting tiles in a position lower than the first element. */
      const basePosition = position > 0 ? position : 1;

      /* Handle the position for tiles with the same position. */
      tilesInUse.set(basePosition, (tilesInUse.get(basePosition) ?? -1) + 1);

      /* Take the base position and add the accumulator for duplicate positions. */
      const duplicatedPosition = basePosition + tilesInUse.get(basePosition);
      const updatedPosition = duplicatedPosition - 1;

      /* Insert into the merged hits array. */
      mergedHits.splice(updatedPosition, 0, tile);
    });

    return mergedHits;
  }

  /**
   * Renders the infinite hits widget with the provided render options.
   * @param {Object} instances - Instances object containing algolia instances.
   * @param {boolean} isFirstRender - Indicates whether this is the first render.
   * @param {Object} renderOptions - Options for rendering the widget.
   * @returns {void}
   */
  render(instances, isFirstRender, renderOptions) {
    if (isFirstRender) return;
  
    const { hits, isFirstPage, isLastPage, showMore, widgetParams } = renderOptions;
    const { container } = widgetParams;
    container.innerHTML = '';
    container.appendChild(this.renderWrapper());

    const loadMoreButton = container.querySelector('button.load-more__btn');

    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', showMore);
      if (isLastPage) loadMoreButton.classList.add('hidden');
      if (!hits.length && isFirstPage && isLastPage) loadMoreButton.classList.add('hidden');
    }

    const mergedHits = this.mergeMarketingTiles(hits);
    const unorderedList = container.querySelector('ul');
    if (unorderedList) {
      mergedHits.forEach((item, index) => {
        if (item instanceof HTMLElement) {
          unorderedList.appendChild(item);
        } else {
          const el = this.renderContent(index, instances, item);
          unorderedList.appendChild(el);
        }
      });
    }

    // this handles sending analytics data for instant search
    aa('init', {
      appId: instances.algoliaShopify.config.app_id,
      apiKey: instances.algoliaShopify.config.search_api_key,
      useCookie: true,
    });

    const anchorTags = [...container.querySelectorAll('a')];
    anchorTags.forEach((anchor, index) => {
      anchor.addEventListener('click', () => {
        const hit = mergedHits[index];
        instances.algoliaShopify.helpers.handleItemClick(hit)
        const objectIds = [hit.objectID];
        const indexPrefix = instances.algoliaShopify.config.index_prefix;
        const algoliaIndex = `${indexPrefix}products`;
        aa('clickedObjectIDsAfterSearch', {
          eventName: 'clicked_product',
          index: algoliaIndex,
          queryID: hit.__queryID,
          objectIDs: objectIds,
          positions: [hit.__position],
        });
      });
    });
  }

  /**
   * Renders the container for the infinite hits widget.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Function} html - The HTML template function.
   * @returns {string} The rendered container.
   */
  renderContainer(configuration, html) {
    const classNames = `${configuration.container} product-grid-container`;
    return html`<div class="${classNames}" id="ProductGridContainer"></div>`;
  }

  /**
   * Renders the content.
   * @param {number} index - The hit index number.
   * @param {Object} instances - Instances object containing algolia instances.
   * @param {Object} item - The hit retrieved.
   * @returns {string} The rendered content HTML.
   */
  renderContent(index, instances, item) {
    item.isLazyLoad = index > 2;
    const hit = this.querySelector('algolia-hit');
    const hitContent = hit ? hit.render(instances, item) : '';
    return hitContent;
  }

  /**
   * Renders the wrapper.
   * @returns {string} The rendered wrapper HTML.
   */
  renderWrapper() {
    const loadMoreButton = this.querySelector('algolia-load-more');
    const loadMoreButtonContent = loadMoreButton ? loadMoreButton.render() : [];
    // const classNames = `${this.settings.productsGridSystemDesktop} product-grid grid--grid grid--fixed-cols-tablet grid--fixed-cols-mobile`;
    const styles = `--product-grid-columns-desktop:${this.settings.columnsDesktop};--product-grid-columns-tablet:${this.settings.columnsTablet};--product-grid-columns-mobile:${this.settings.columnsMobile};`;

    const collectionDiv = document.createElement('div');
    collectionDiv.classList.add('collection');
    const loadingOverlayDiv = document.createElement('div');
    loadingOverlayDiv.classList.add('loading-overlay', 'gradient');

    collectionDiv.appendChild(loadingOverlayDiv);

    const ul = document.createElement('ul');
    ul.id = 'algolia-product-grid';
    ul.classList.add(
      this.settings.productsGridSystemDesktop,
      'product-grid',
      'grid--grid',
      'grid--fixed-cols-tablet',
      'grid--fixed-cols-mobile'
    );
    ul.style = styles;

    collectionDiv.appendChild(ul);
    loadMoreButtonContent.forEach(element => collectionDiv.appendChild(element));

    return collectionDiv;
  }
}

customElements.define("algolia-infinite-hits", AlgoliaInfiniteHits);
